import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  Tooltip,
  Typography,
  createTheme,
  ThemeProvider,
} from '@mui/material';
import { OpenInNew, Refresh } from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { getMinAndMaxDate, getColumnsUniqueValues, filterProjectOutOfStockData, refreshProjectDistributionData } from './api';
import {
  DISTRIBUTION_VIEW_ROUTE_SEGMENT,
  getProductViewAccent,
  isDistributionViewGroupBySegment,
} from './productViewProjectConfig';
import { useAuth } from './AuthContext';
import KpiElements from './KpiElement';
import BarPlot from './BarPlot';

const compactFieldSx = {
  '& .MuiInputBase-root': { height: 36, fontSize: '0.8rem' },
  '& .MuiInputBase-input': { padding: '6px 10px' },
  '& .MuiInputLabel-root': { fontSize: '0.75rem' },
};

const DISTRIBUTION_DEFAULT_WINDOW_DAYS = 7;
const DISTRIBUTION_PICKER_FLOOR = dayjs('2025-01-01');

function defaultWindowStart(end) {
  if (!end) return dayjs().subtract(DISTRIBUTION_DEFAULT_WINDOW_DAYS - 1, 'day');
  return end.subtract(DISTRIBUTION_DEFAULT_WINDOW_DAYS - 1, 'day');
}

function DistributionView() {
  const { projectKey, groupBySegment } = useParams();
  const navigate = useNavigate();
  const isBrandScope = groupBySegment !== 'retailers';
  const viewType = isBrandScope ? 'distribution_view_brand' : 'distribution_view_retailer';

  const { uiStyle } = useAuth();
  const accent = useMemo(() => getProductViewAccent(projectKey, uiStyle), [projectKey, uiStyle]);
  const theme = useMemo(
    () => createTheme({ palette: { primary: { main: accent.main } } }),
    [accent.main]
  );

  const [columnsUniqueValues, setColumnsUniqueValues] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedRetailer, setSelectedRetailer] = useState('');
  const [selectedRetailers, setSelectedRetailers] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [oosWhileInSaleStatus, setOosWhileInSaleStatus] = useState(false);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [minDate, setMinDate] = useState(null);
  const [maxDate, setMaxDate] = useState(null);
  const [dateSliderValue, setDateSliderValue] = useState([0, 0]);

  const [barData, setBarData] = useState(null);
  const [gridData, setGridData] = useState(null);
  const [kpiData, setKpiData] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isDbRefreshing, setIsDbRefreshing] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const lastFetchedRangeRef = useRef(null);
  const autoRefreshAttemptedRef = useRef(false);
  const [metadataReady, setMetadataReady] = useState(false);

  useEffect(() => {
    if (!groupBySegment) return;
    if (!isDistributionViewGroupBySegment(groupBySegment)) {
      navigate(`/${projectKey}/${DISTRIBUTION_VIEW_ROUTE_SEGMENT}/brands`, { replace: true });
    }
  }, [groupBySegment, projectKey, navigate]);

  const availableDates = useMemo(() => {
    if (!minDate || !maxDate) return [];
    const dates = [];
    let current = minDate.startOf('day');
    const end = maxDate.startOf('day');
    while (current.isBefore(end) || current.isSame(end, 'day')) {
      dates.push(current);
      current = current.add(1, 'day');
    }
    return dates;
  }, [minDate, maxDate]);

  useEffect(() => {
    if (!availableDates.length || !startDate || !endDate) return;
    const startIdx = availableDates.findIndex((d) => d.isSame(startDate, 'day'));
    const endIdx = availableDates.findIndex((d) => d.isSame(endDate, 'day'));
    setDateSliderValue([
      startIdx === -1 ? 0 : startIdx,
      endIdx === -1 ? availableDates.length - 1 : endIdx,
    ]);
  }, [availableDates, startDate, endDate]);

  const handleDateSliderChange = (_, newValue) => {
    setDateSliderValue(newValue);
  };

  const handleDateSliderCommit = (_, newValue) => {
    if (!availableDates.length) return;
    setStartDate(availableDates[newValue[0]]);
    setEndDate(availableDates[newValue[1]]);
  };

  const handleRetailersChange = (event) => {
    const { value } = event.target;
    let next = typeof value === 'string' ? value.split(',') : value;
    const allRetailers = columnsUniqueValues?.Retailer || [];
    if (next.includes('all')) {
      next = next.length >= allRetailers.length ? [] : allRetailers;
    }
    setSelectedRetailers(next);
  };

  const handleBrandsChange = (event) => {
    const { value } = event.target;
    let next = typeof value === 'string' ? value.split(',') : value;
    const allBrands = columnsUniqueValues?.Brand || [];
    if (next.includes('all')) {
      next = next.length >= allBrands.length ? [] : allBrands;
    }
    setSelectedBrands(next);
  };

  const handleStatusesChange = (event) => {
    const { value } = event.target;
    let next = typeof value === 'string' ? value.split(',') : value;
    const allStatuses = columnsUniqueValues?.Status || [];
    if (next.includes('all')) {
      next = next.length >= allStatuses.length ? [] : allStatuses;
    }
    setSelectedStatuses(next);
  };

  const handleResetFilters = () => {
    setSelectedStatuses([]);
    setOosWhileInSaleStatus(false);
    if (isBrandScope) {
      setSelectedBrand(columnsUniqueValues?.Brand?.[0] || '');
      setSelectedRetailers([]);
    } else {
      setSelectedRetailer(columnsUniqueValues?.Retailer?.[0] || '');
      setSelectedBrands([]);
    }
    if (minDate && maxDate) {
      const weekStart = defaultWindowStart(maxDate);
      setStartDate(weekStart.isBefore(minDate) ? minDate : weekStart);
      setEndDate(maxDate);
    }
  };

  const handleDataRefresh = useCallback(async () => {
    setIsDbRefreshing(true);
    const startDateString = startDate
      ? startDate.format('YYYY-MM-DD')
      : defaultWindowStart(dayjs()).format('YYYY-MM-DD');
    const endDateString = endDate ? endDate.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
    lastFetchedRangeRef.current = `${startDateString}:${endDateString}`;
    const result = await refreshProjectDistributionData(projectKey, startDateString, endDateString);
    if (result?.success) {
      setRefreshCounter((prev) => prev + 1);
    } else {
      console.error(`Failed to refresh ${projectKey} distribution data from database.`);
    }
    setIsDbRefreshing(false);
  }, [projectKey, startDate, endDate]);

  useEffect(() => {
    setSelectedBrand('');
    setSelectedBrands([]);
    setSelectedRetailer('');
    setSelectedRetailers([]);
    setSelectedStatuses([]);
    setOosWhileInSaleStatus(false);
    setBarData(null);
    setGridData(null);
    setKpiData(null);
    setMetadataReady(false);
  }, [groupBySegment]);

  useEffect(() => {
    const fetchMetadata = async () => {
      const dateResult = await getMinAndMaxDate(projectKey, viewType);
      const hasDates = dateResult?.min_date && dateResult?.max_date;

      if (!hasDates && !autoRefreshAttemptedRef.current) {
        autoRefreshAttemptedRef.current = true;
        setIsDbRefreshing(true);
        const fallbackEnd = dayjs();
        const fallbackStart = defaultWindowStart(fallbackEnd);
        const fallbackStartStr = fallbackStart.format('YYYY-MM-DD');
        const fallbackEndStr = fallbackEnd.format('YYYY-MM-DD');
        lastFetchedRangeRef.current = `${fallbackStartStr}:${fallbackEndStr}`;
        await refreshProjectDistributionData(projectKey, fallbackStartStr, fallbackEndStr);
        setIsDbRefreshing(false);
        setRefreshCounter((prev) => prev + 1);
        return;
      }

      if (hasDates) {
        const newMinDate = dayjs(dateResult.min_date);
        const newMaxDate = dayjs(dateResult.max_date);
        if (newMinDate.isValid() && newMaxDate.isValid()) {
          setMinDate(newMinDate);
          setMaxDate(newMaxDate);
          setStartDate((prev) => {
            if (prev) return prev;
            const weekStart = defaultWindowStart(newMaxDate);
            return weekStart.isBefore(newMinDate) ? newMinDate : weekStart;
          });
          setEndDate((prev) => prev || newMaxDate);
        }
      }

      const uniqueVals = await getColumnsUniqueValues(projectKey, viewType);
      if (uniqueVals) {
        setColumnsUniqueValues(uniqueVals);
        if (isBrandScope) {
          setSelectedBrand((prev) => prev || uniqueVals.Brand?.[0] || '');
        } else {
          setSelectedRetailer((prev) => prev || uniqueVals.Retailer?.[0] || '');
        }
      }
      setMetadataReady(true);
    };

    fetchMetadata();
  }, [projectKey, refreshCounter, viewType, isBrandScope, groupBySegment]);

  useEffect(() => {
    if (!metadataReady || !startDate || !endDate) return;
    const startStr = startDate.format('YYYY-MM-DD');
    const endStr = endDate.format('YYYY-MM-DD');
    const rangeKey = `${startStr}:${endStr}`;
    if (lastFetchedRangeRef.current === rangeKey) return;

    let cancelled = false;
    const loadMissingWeeks = async () => {
      lastFetchedRangeRef.current = rangeKey;
      setIsDbRefreshing(true);
      const result = await refreshProjectDistributionData(projectKey, startStr, endStr);
      if (cancelled) return;
      setIsDbRefreshing(false);
      if (result?.success && result.fetched_days > 0) {
        setRefreshCounter((prev) => prev + 1);
      }
    };
    loadMissingWeeks();
    return () => {
      cancelled = true;
    };
  }, [metadataReady, startDate, endDate, projectKey]);

  useEffect(() => {
    if (!metadataReady) return;

    const fetchChartAndGrid = async () => {
      setIsRefreshing(true);
      const startDateString = startDate ? startDate.format('YYYY-MM-DD') : null;
      const endDateString = endDate ? endDate.format('YYYY-MM-DD') : null;
      const retailers = isBrandScope
        ? selectedRetailers
        : (selectedRetailer ? [selectedRetailer] : []);
      const brands = isBrandScope
        ? (selectedBrand ? [selectedBrand] : [])
        : selectedBrands;
      const data = await filterProjectOutOfStockData(
        projectKey,
        viewType,
        retailers,
        brands,
        selectedStatuses,
        oosWhileInSaleStatus,
        startDateString,
        endDateString
      );

      if (data) {
        setBarData(data.graph_data);
        setGridData(data.grid_data);
        setKpiData(data.kpi_data);
        setColumnsUniqueValues((prev) => ({ ...(prev || {}), ...data.columns_unique_values }));
      }
      setIsRefreshing(false);
    };

    fetchChartAndGrid();
  }, [
    projectKey,
    viewType,
    isBrandScope,
    metadataReady,
    selectedBrand,
    selectedBrands,
    selectedRetailer,
    selectedRetailers,
    selectedStatuses,
    oosWhileInSaleStatus,
    startDate,
    endDate,
    refreshCounter,
  ]);

  const columnsWithClickableUrls = (gridData?.columns || []).map((col) => {
    if (col.field === 'url') {
      return {
        ...col,
        minWidth: 70,
        flex: 0.4,
        renderCell: (params) => (
          params.value ? (
            <a href={params.value} target="_blank" rel="noopener noreferrer" title={params.value}>
              <OpenInNew fontSize="small" sx={{ color: accent.main, mt: 0.75 }} />
            </a>
          ) : null
        ),
      };
    }
    if (col.field === 'in_sale') {
      return {
        ...col,
        minWidth: 80,
        renderCell: (params) => (params.value === 1 || params.value === true || params.value === 'Yes' ? 'Yes' : 'No'),
      };
    }
    if (col.field === 'title') {
      return { ...col, minWidth: 220, flex: 1.4 };
    }
    return { ...col, minWidth: 90 };
  });

  const brandSelect = isBrandScope ? (
    <FormControl fullWidth size="small" sx={compactFieldSx}>
      <InputLabel id="distribution-brand-label">Brand</InputLabel>
      <Select
        labelId="distribution-brand-label"
        value={selectedBrand}
        label="Brand"
        onChange={(e) => setSelectedBrand(e.target.value)}
        MenuProps={{ PaperProps: { style: { maxHeight: 400 } } }}
      >
        {(columnsUniqueValues?.Brand || []).map((brand) => (
          <MenuItem key={brand} value={brand}>{brand}</MenuItem>
        ))}
      </Select>
    </FormControl>
  ) : (
    <Tooltip title={!selectedRetailer ? 'Select Retailer first' : ''} arrow>
      <FormControl fullWidth size="small" sx={compactFieldSx} disabled={!selectedRetailer}>
        <InputLabel id="distribution-brands-label">Brand</InputLabel>
        <Select
          labelId="distribution-brands-label"
          multiple
          value={selectedBrands}
          label="Brand"
          onChange={handleBrandsChange}
          renderValue={(selected) => (selected.length ? selected.join(', ') : 'All')}
          MenuProps={{ PaperProps: { style: { maxHeight: 400 } } }}
          displayEmpty
        >
          <MenuItem value="all">Select all</MenuItem>
          {(columnsUniqueValues?.Brand || []).map((brand) => (
            <MenuItem key={brand} value={brand}>{brand}</MenuItem>
          ))}
        </Select>
      </FormControl>
    </Tooltip>
  );

  const retailerSelect = isBrandScope ? (
    <Tooltip title={!selectedBrand ? 'Select Brand first' : ''} arrow>
      <FormControl fullWidth size="small" sx={compactFieldSx} disabled={!selectedBrand}>
        <InputLabel id="distribution-retailer-label">Retailer</InputLabel>
        <Select
          labelId="distribution-retailer-label"
          multiple
          value={selectedRetailers}
          label="Retailer"
          onChange={handleRetailersChange}
          renderValue={(selected) => (selected.length ? selected.join(', ') : 'All')}
          MenuProps={{ PaperProps: { style: { maxHeight: 400 } } }}
          displayEmpty
        >
          <MenuItem value="all">Select all</MenuItem>
          {(columnsUniqueValues?.Retailer || []).map((retailer) => (
            <MenuItem key={retailer} value={retailer}>{retailer}</MenuItem>
          ))}
        </Select>
      </FormControl>
    </Tooltip>
  ) : (
    <FormControl fullWidth size="small" sx={compactFieldSx}>
      <InputLabel id="distribution-retailer-single-label">Retailer</InputLabel>
      <Select
        labelId="distribution-retailer-single-label"
        value={selectedRetailer}
        label="Retailer"
        onChange={(e) => setSelectedRetailer(e.target.value)}
        MenuProps={{ PaperProps: { style: { maxHeight: 400 } } }}
      >
        {(columnsUniqueValues?.Retailer || []).map((retailer) => (
          <MenuItem key={retailer} value={retailer}>{retailer}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <div style={{ width: '100%', maxWidth: '100%', marginInline: 'auto', padding: '0 20px' }}>
      <ThemeProvider theme={theme}>
        <Typography variant="h6" sx={{ mt: 1.5, mb: 1, fontWeight: 700, color: accent.main }}>
          Distribution View — {isBrandScope ? 'By Brands' : 'By Retailers'}
        </Typography>

        <Paper
          elevation={0}
          sx={{ p: 1.25, mb: 2, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(140px, 1fr) minmax(160px, 1.2fr) minmax(140px, 1fr) auto auto auto',
              gap: 1.25,
              alignItems: 'start',
            }}
          >
            {isBrandScope ? brandSelect : retailerSelect}
            {isBrandScope ? retailerSelect : brandSelect}

            <FormControl fullWidth size="small" sx={compactFieldSx}>
              <InputLabel id="distribution-status-label">Status</InputLabel>
              <Select
                labelId="distribution-status-label"
                multiple
                value={selectedStatuses}
                label="Status"
                onChange={handleStatusesChange}
                renderValue={(selected) => (selected.length ? selected.join(', ') : 'All')}
                MenuProps={{ PaperProps: { style: { maxHeight: 400 } } }}
                displayEmpty
              >
                <MenuItem value="all">Select all</MenuItem>
                {(columnsUniqueValues?.Status || []).map((status) => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant={oosWhileInSaleStatus ? 'contained' : 'outlined'}
              onClick={() => setOosWhileInSaleStatus((prev) => !prev)}
              sx={{
                whiteSpace: 'nowrap',
                minWidth: 'fit-content',
                height: 36,
                fontSize: '0.75rem',
                borderColor: accent.main,
                color: oosWhileInSaleStatus ? '#fff' : accent.main,
                backgroundColor: oosWhileInSaleStatus ? accent.main : 'transparent',
                '&:hover': {
                  backgroundColor: oosWhileInSaleStatus ? accent.hover : 'rgba(0,0,0,0.04)',
                  borderColor: accent.main,
                },
              }}
            >
              OOS while In Sale
            </Button>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                  <DatePicker
                    label="From"
                    value={startDate}
                    onChange={setStartDate}
                    format="D.M.YYYY"
                    minDate={DISTRIBUTION_PICKER_FLOOR}
                    maxDate={dayjs()}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: {
                          width: 130,
                          '& .MuiInputBase-root': { height: 36, fontSize: '0.8rem' },
                        },
                      },
                    }}
                  />
                  <DatePicker
                    label="To"
                    value={endDate}
                    onChange={setEndDate}
                    format="D.M.YYYY"
                    minDate={DISTRIBUTION_PICKER_FLOOR}
                    maxDate={dayjs()}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: {
                          width: 130,
                          '& .MuiInputBase-root': { height: 36, fontSize: '0.8rem' },
                        },
                      },
                    }}
                  />
                </Box>
                <Box sx={{ width: 268, px: 0.5 }}>
                  <Slider
                    value={Array.isArray(dateSliderValue) ? dateSliderValue : [dateSliderValue, dateSliderValue]}
                    onChange={handleDateSliderChange}
                    onChangeCommitted={handleDateSliderCommit}
                    min={0}
                    max={Math.max(availableDates.length - 1, 0)}
                    step={1}
                    disableSwap
                    valueLabelDisplay="off"
                    disabled={!availableDates.length}
                    sx={{
                      color: accent.main,
                      '& .MuiSlider-track': { backgroundColor: accent.main },
                      '& .MuiSlider-rail': { backgroundColor: accent.rail },
                      '& .MuiSlider-thumb': { borderColor: accent.main },
                    }}
                  />
                </Box>
              </Box>
            </LocalizationProvider>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Button
                variant="contained"
                onClick={handleDataRefresh}
                disabled={isDbRefreshing}
                startIcon={isDbRefreshing ? <CircularProgress size={12} color="inherit" /> : <Refresh sx={{ fontSize: '1rem' }} />}
                sx={{
                  backgroundColor: accent.main,
                  '&:hover': { backgroundColor: accent.hover },
                  fontWeight: 500,
                  fontSize: '0.7rem',
                  whiteSpace: 'nowrap',
                  height: 36,
                  minWidth: 120,
                }}
              >
                {isDbRefreshing ? 'Refreshing...' : 'Data Refresh'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleResetFilters}
                sx={{
                  fontSize: '0.7rem',
                  height: 32,
                  minWidth: 120,
                  borderColor: accent.main,
                  color: accent.main,
                }}
              >
                Reset Filters
              </Button>
            </Box>
          </Box>
        </Paper>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: '1 1 auto', minWidth: 0 }}>
            {isRefreshing && !barData ? (
              <Box sx={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={28} sx={{ color: accent.main }} />
              </Box>
            ) : (
              BarPlot(barData, 'Products total', '#', 'Additions / Removals', '#', 'Product')
            )}

            <Box>
              {gridData ? (
                <DataGrid
                  rowHeight={35}
                  rows={gridData.rows}
                  columns={columnsWithClickableUrls.map((column) => ({
                    ...column,
                    resizable: true,
                    headerAlign: 'center',
                  }))}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 100 } },
                  }}
                  pageSizeOptions={[25, 50, 100]}
                  disableSelectionOnClick
                  density="compact"
                  sx={{
                    height: 350,
                    backgroundColor: '#fff',
                    border: '1px solid #e0e0e0',
                  }}
                />
              ) : (
                <Box sx={{ height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
                  {isDbRefreshing || isRefreshing ? 'Loading...' : 'No distribution data yet. Click Data Refresh to load product statuses.'}
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ width: 220, flexShrink: 0 }}>
            {KpiElements(kpiData)}
          </Box>
        </Box>
      </ThemeProvider>
    </div>
  );
}

export default DistributionView;
